import { ComponentProps, FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";

import {
  Drawer,
  Divider,
  Typography,
  Space,
  Button,
  FireOutlined,
  AntDesignSelect,
  Row,
  Col,
  message
} from "@ctra/components";

import { Admin } from "@ctra/api";

const { Paragraph } = Typography;

const titles = [
  "Control Freak Central",
  "The Panic Room",
  "Proceed With Caution",
  "Danger Zone",
  "Don't Break Stuff",
  "Secret Lair",
  "Mission Control",
  "The Power Throne",
  "Abandon Hope",
  "Wizard's Workshop",
  "Caution: Hot Coffee",
  "The Big Red Button",
  "Chaos Manager",
  "Look What You Did",
  "Troublemaker's Toolkit",
  "System Override"
];

/**
 * Generic widget component
 * @param {string} title
 * @param {string} description
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 * @constructor
 */
const Widget: FC<{ title: string; description: string }> = ({ title, description, children }) => {
  return (
    <div>
      <Divider>{title}</Divider>
      <Paragraph>{description}</Paragraph>
      {children}
    </div>
  );
};

/**
 * Reset parts of the app state
 * @returns {JSX.Element}
 * @constructor
 */
const ResetOMatic: FC = () => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<string | null>(null);
  const state = useSelector((state) => state);

  return (
    <Widget
      title="Reset-o-Matic"
      description="Because sometimes turning it off and on again is just too much work."
    >
      <Row gutter={[8, 16]}>
        <Col flex={1}>
          <AntDesignSelect
            style={{ width: "100%" }}
            placeholder="Select a key in the state to reset"
            onChange={(value) => {
              setSelected(value);
            }}
            options={[
              {
                label: "Presets",
                title: <span>Presets</span>,
                options: [{ label: "Chart related", value: _.join(["charts", "dataDictionary", "async"]) }]
              },
              {
                label: "Single fields",
                title: <span>Single fields</span>,
                options: _.map(_.keys(state), (v) => ({
                  label: _.lowerCase(v),
                  value: v
                }))
              }
            ]}
          ></AntDesignSelect>
        </Col>
        <Col>
          <Button
            disabled={!selected}
            onClick={() => {
              if (selected) {
                const list = _.split(selected, ",");
                const joined = _.join(list, ", ");
                const plural = _.size(list) > 1;

                const msg = _.join(
                  [plural ? "Fields" : "Field", joined, plural ? "are" : "is", "flushed from the state."],
                  " "
                );

                dispatch(Admin.actions.flush(list));
                message.success(msg);
              }
            }}
          >
            Flush
          </Button>
        </Col>
      </Row>
    </Widget>
  );
};

/**
 * Admin tools component
 * @param {any} open
 * @param {any} onClose
 * @param {Omit<React.PropsWithChildren<DrawerProps | {}>, "open" | "onClose">} props
 * @returns {JSX.Element}
 * @constructor
 */
export const AdminTools: FC<ComponentProps<typeof Drawer>> = ({ open, onClose, ...props }) => {
  return (
    <Drawer
      title={_.sample(titles)}
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      closable={false}
      extra={
        <Space>
          <Button onClick={onClose} icon={<FireOutlined />} />
        </Space>
      }
      {...props}
    >
      <Paragraph>
        Behold, the Powers You Shouldn&apos;t Have! Please remember that with great admin tools comes
        absolutely zero accountability.
      </Paragraph>
      <ResetOMatic />
    </Drawer>
  );
};
